import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Target, ArrowLeft, Plus, Trash2, QrCode, Link as LinkIcon, FileText,
  Link2, RefreshCw, Play, Pause, MoreVertical, Settings, BarChart3,
  Users, MousePointerClick, Eye, Calendar, Tag, Check, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import campaignsApi from '@/api/campaigns';
import { apiV1 } from '@/api/client';
import { FEATURES } from '@/lib/features';

const statusColors = {
  draft: 'bg-slate-500',
  scheduled: 'bg-blue-500',
  active: 'bg-emerald-500',
  paused: 'bg-amber-500',
  completed: 'bg-purple-500',
  archived: 'bg-gray-500',
};

const assetTypeConfig = {
  qr_code: { label: 'QR Codes', icon: QrCode, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  short_url: { label: 'Short URLs', icon: LinkIcon, color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
  landing_page: { label: 'Landing Pages', icon: FileText, color: 'text-purple-600', bgColor: 'bg-purple-50' },
  bio_link: { label: 'Bio Links', icon: Link2, color: 'text-amber-600', bgColor: 'bg-amber-50' },
};

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [performance, setPerformance] = useState(null);
  const [showAddAssetDialog, setShowAddAssetDialog] = useState(false);
  const [assetType, setAssetType] = useState('qr_code');
  const [availableAssets, setAvailableAssets] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');
  const [linkedAssets, setLinkedAssets] = useState({
    qr_codes: [],
    short_urls: [],
    landing_pages: [],
    bio_links: [],
  });

  useEffect(() => {
    if (id) {
      loadCampaign();
      loadPerformance();
    }
  }, [id]);

  const loadCampaign = async () => {
    setLoading(true);
    try {
      const result = await campaignsApi.get(id);
      if (result.success) {
        setCampaign(result.data);
        loadLinkedAssets();
      } else {
        toast.error('Campaign not found');
        navigate('/campaigns');
      }
    } catch (error) {
      toast.error('Failed to load campaign');
    } finally {
      setLoading(false);
    }
  };

  const loadPerformance = async () => {
    try {
      const result = await campaignsApi.getPerformance(id);
      if (result.success) {
        setPerformance(result.data);
      }
    } catch (error) {
      console.error('Failed to load performance:', error);
    }
  };

  // The campaign's own linked assets, from /campaigns/{id}/assets. This used
  // to call the list endpoints with an ``ids`` param they ignore, so the page
  // showed the user's first 50 assets of each type as "linked".
  const loadLinkedAssets = async () => {
    try {
      const result = await campaignsApi.assets(id);
      if (result.success) {
        setLinkedAssets({
          qr_codes: result.data.qr_codes || [],
          short_urls: result.data.short_urls || [],
          landing_pages: result.data.landing_pages || [],
          bio_links: result.data.bio_links || [],
        });
      }
    } catch (error) {
      console.error('Failed to load linked assets:', error);
    }
  };

  const loadAvailableAssets = async (type, search = '') => {
    setLoadingAssets(true);
    try {
      // Each list API pages differently: qrcodes/urls/pages take page/per_page
      // (a ``limit`` is ignored), bio pages live at /bio/pages with skip/limit.
      const endpointMap = {
        qr_code: ['/qrcodes/', { per_page: 200 }],
        short_url: ['/urls/', { per_page: 200 }],
        landing_page: ['/pages/', { per_page: 200 }],
        bio_link: ['/bio/pages', { limit: 100 }],
      };
      const [path, paging] = endpointMap[type];
      const params = { ...paging, ...(search ? { search } : {}) };

      const response = await apiV1.get(path, { params });
      if (response.data?.success) {
        const d = response.data.data;
        const rows = Array.isArray(d) ? d : (d?.items || []);
        // Filter out already linked assets
        const linkedIds = campaign?.assets?.[`${type}_ids`] || [];
        setAvailableAssets(rows.filter(a => !linkedIds.includes(a.id)));
      }
    } catch (error) {
      console.error('Failed to load assets:', error);
      setAvailableAssets([]);
    } finally {
      setLoadingAssets(false);
    }
  };

  const handleOpenAddAssetDialog = (type) => {
    setAssetType(type);
    setSelectedAssets([]);
    setAssetSearch('');
    setShowAddAssetDialog(true);
    loadAvailableAssets(type);
  };

  const handleAddAssets = async () => {
    if (selectedAssets.length === 0) {
      toast.error('Select at least one asset');
      return;
    }

    try {
      for (const assetId of selectedAssets) {
        await campaignsApi.addAsset(id, assetType, assetId);
      }
      toast.success(`${selectedAssets.length} asset(s) added to campaign!`);
      setShowAddAssetDialog(false);
      loadCampaign();
      loadPerformance();
    } catch (error) {
      toast.error('Failed to add assets');
    }
  };

  const handleRemoveAsset = async (type, assetId) => {
    if (!window.confirm('Remove this asset from the campaign?')) return;
    try {
      await campaignsApi.removeAsset(id, type, assetId);
      toast.success('Asset removed from campaign');
      loadCampaign();
      loadPerformance();
    } catch (error) {
      toast.error('Failed to remove asset');
    }
  };

  const handleRefreshStats = async () => {
    try {
      await campaignsApi.refreshStats(id);
      toast.success('Statistics refreshed!');
      loadCampaign();
      loadPerformance();
    } catch (error) {
      toast.error('Failed to refresh statistics');
    }
  };

  const handleActivate = async () => {
    try {
      await campaignsApi.activate(id);
      toast.success('Campaign activated!');
      loadCampaign();
    } catch (error) {
      toast.error('Failed to activate campaign');
    }
  };

  const handlePause = async () => {
    try {
      await campaignsApi.pause(id);
      toast.success('Campaign paused');
      loadCampaign();
    } catch (error) {
      toast.error('Failed to pause campaign');
    }
  };

  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev =>
      prev.includes(assetId) ? prev.filter(id => id !== assetId) : [...prev, assetId]
    );
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="campaign-detail-loading">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/3 mb-4" />
          <div className="h-4 bg-muted rounded w-1/2" />
        </div>
      </div>
    );
  }

  if (!campaign) {
    return null;
  }

  const AssetCard = ({ asset, type, onRemove }) => {
    const config = assetTypeConfig[type];
    const Icon = config.icon;
    return (
      <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 group">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${config.bgColor} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${config.color}`} />
          </div>
          <div>
            <p className="font-medium">{asset.name || asset.title || asset.display_name || asset.short_code || asset.slug || 'Unnamed'}</p>
            <p className="text-xs text-muted-foreground">
              {asset.total_scans ? `${asset.total_scans} scans` : 
               asset.total_clicks ? `${asset.total_clicks} clicks` :
               asset.total_views ? `${asset.total_views} views` : 'No data yet'}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={() => onRemove(type, asset.id)}
        >
          <Trash2 className="w-4 h-4 text-destructive" />
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="campaign-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/campaigns')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-3xl font-bold text-foreground">{campaign.name}</h1>
              <Badge className={`${statusColors[campaign.status]} text-white`}>
                {campaign.status}
              </Badge>
            </div>
            {campaign.description && (
              <p className="text-muted-foreground">{campaign.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleRefreshStats}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Stats
          </Button>
          {campaign.status === 'draft' || campaign.status === 'paused' ? (
            <Button onClick={handleActivate}>
              <Play className="w-4 h-4 mr-2" />
              Activate
            </Button>
          ) : campaign.status === 'active' ? (
            <Button variant="outline" onClick={handlePause}>
              <Pause className="w-4 h-4 mr-2" />
              Pause
            </Button>
          ) : null}
        </div>
      </div>

      {/* Stats: computed live from the linked assets' events inside the
          campaign dates (all time when no dates), bots excluded. */}
      <p className="text-xs text-muted-foreground" data-testid="campaign-stats-window">
        {[
          "Figures for this campaign's linked assets",
          campaign.stats?.window_start ? ` from ${new Date(campaign.stats.window_start).toLocaleDateString()}` : '',
          campaign.end_date && campaign.stats?.window_end ? ` to ${new Date(campaign.stats.window_end).toLocaleDateString()}` : '',
          '. Bot traffic excluded.',
        ].join('')}
      </p>
      <div className="grid grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Scans</p>
                <p className="text-2xl font-bold">{campaign.stats?.total_scans || 0}</p>
              </div>
              <QrCode className="w-8 h-8 text-blue-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Clicks</p>
                <p className="text-2xl font-bold">{campaign.stats?.total_clicks || 0}</p>
              </div>
              <MousePointerClick className="w-8 h-8 text-emerald-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Page Views</p>
                <p className="text-2xl font-bold">{campaign.stats?.total_page_views || 0}</p>
              </div>
              <Eye className="w-8 h-8 text-purple-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Leads</p>
                <p className="text-2xl font-bold text-primary">{campaign.stats?.total_leads || 0}</p>
              </div>
              <Users className="w-8 h-8 text-primary/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground" title="Form submissions / unique landing page views">Conversion</p>
                <p className="text-2xl font-bold">{campaign.stats?.conversion_rate || 0}%</p>
                {/* Scoped conversions inbox, only when conversion goals are switched on */}
                {FEATURES.conversionGoals && (
                  <Link
                    to={`/conversions?campaign_id=${id}`}
                    className="text-xs text-primary hover:underline mt-1 inline-block"
                  >
                    View conversions →
                  </Link>
                )}
              </div>
              <BarChart3 className="w-8 h-8 text-amber-500/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Assets Section */}
      <Tabs defaultValue="qr_codes" className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="qr_codes" className="flex items-center gap-2">
              <QrCode className="w-4 h-4" />
              QR Codes ({campaign.assets?.qr_code_ids?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="urls" className="flex items-center gap-2">
              <LinkIcon className="w-4 h-4" />
              URLs ({campaign.assets?.short_url_ids?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="pages" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Pages ({campaign.assets?.landing_page_ids?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="bio" className="flex items-center gap-2">
              <Link2 className="w-4 h-4" />
              Bio Links ({campaign.assets?.bio_link_ids?.length || 0})
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="qr_codes">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Linked QR Codes</CardTitle>
                <CardDescription>QR codes associated with this campaign</CardDescription>
              </div>
              <Button onClick={() => handleOpenAddAssetDialog('qr_code')} data-testid="add-qr-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add QR Code
              </Button>
            </CardHeader>
            <CardContent>
              {linkedAssets.qr_codes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <QrCode className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No QR codes linked yet</p>
                  <Button variant="outline" className="mt-4" onClick={() => handleOpenAddAssetDialog('qr_code')}>
                    Add QR Code
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {linkedAssets.qr_codes.map(asset => (
                    <AssetCard key={asset.id} asset={asset} type="qr_code" onRemove={handleRemoveAsset} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="urls">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Linked Short URLs</CardTitle>
                <CardDescription>Short URLs associated with this campaign</CardDescription>
              </div>
              <Button onClick={() => handleOpenAddAssetDialog('short_url')} data-testid="add-url-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add URL
              </Button>
            </CardHeader>
            <CardContent>
              {linkedAssets.short_urls.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <LinkIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No short URLs linked yet</p>
                  <Button variant="outline" className="mt-4" onClick={() => handleOpenAddAssetDialog('short_url')}>
                    Add URL
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {linkedAssets.short_urls.map(asset => (
                    <AssetCard key={asset.id} asset={asset} type="short_url" onRemove={handleRemoveAsset} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pages">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Linked Landing Pages</CardTitle>
                <CardDescription>Landing pages associated with this campaign</CardDescription>
              </div>
              <Button onClick={() => handleOpenAddAssetDialog('landing_page')} data-testid="add-page-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Page
              </Button>
            </CardHeader>
            <CardContent>
              {linkedAssets.landing_pages.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No landing pages linked yet</p>
                  <Button variant="outline" className="mt-4" onClick={() => handleOpenAddAssetDialog('landing_page')}>
                    Add Page
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {linkedAssets.landing_pages.map(asset => (
                    <AssetCard key={asset.id} asset={asset} type="landing_page" onRemove={handleRemoveAsset} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bio">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Linked Bio Links</CardTitle>
                <CardDescription>Bio links associated with this campaign</CardDescription>
              </div>
              <Button onClick={() => handleOpenAddAssetDialog('bio_link')} data-testid="add-bio-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Bio Link
              </Button>
            </CardHeader>
            <CardContent>
              {linkedAssets.bio_links.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Link2 className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No bio links linked yet</p>
                  <Button variant="outline" className="mt-4" onClick={() => handleOpenAddAssetDialog('bio_link')}>
                    Add Bio Link
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {linkedAssets.bio_links.map(asset => (
                    <AssetCard key={asset.id} asset={asset} type="bio_link" onRemove={handleRemoveAsset} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Asset Dialog */}
      <Dialog open={showAddAssetDialog} onOpenChange={setShowAddAssetDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add {assetTypeConfig[assetType]?.label} to Campaign</DialogTitle>
            <DialogDescription>
              Select assets to link to this campaign. Their stats will be aggregated.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => { e.preventDefault(); loadAvailableAssets(assetType, assetSearch.trim()); }}
            className="flex gap-2"
          >
            <Input
              placeholder="Search by name"
              value={assetSearch}
              onChange={(e) => setAssetSearch(e.target.value)}
              data-testid="asset-search"
            />
            <Button type="submit" variant="outline">Search</Button>
          </form>
          <div className="py-4 max-h-[400px] overflow-auto">
            {loadingAssets ? (
              <div className="text-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground mt-2">Loading assets...</p>
              </div>
            ) : availableAssets.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No available assets to add</p>
              </div>
            ) : (
              <div className="space-y-2">
                {availableAssets.map(asset => {
                  const config = assetTypeConfig[assetType];
                  const Icon = config.icon;
                  const isSelected = selectedAssets.includes(asset.id);
                  return (
                    <div
                      key={asset.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        isSelected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                      }`}
                      onClick={() => toggleAssetSelection(asset.id)}
                    >
                      <Checkbox checked={isSelected} />
                      <div className={`w-10 h-10 rounded-lg ${config.bgColor} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${config.color}`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{asset.name || asset.title || asset.display_name || asset.short_code || asset.slug || 'Unnamed'}</p>
                        <p className="text-xs text-muted-foreground">
                          {asset.destination_url || asset.slug || asset.id}
                        </p>
                      </div>
                      {isSelected && <Check className="w-5 h-5 text-primary" />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAssetDialog(false)}>Cancel</Button>
            <Button onClick={handleAddAssets} disabled={selectedAssets.length === 0} data-testid="add-assets-submit">
              Add {selectedAssets.length} Asset{selectedAssets.length !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
